import { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import DashNav from "@/components/chalann/DashNav";
import VoicePanel from "@/components/chalann/VoicePanel";
import { SubscriptionGate } from "@/components/chalann/SubscriptionGate";
import QRReceiptModal from "@/components/chalann/QRReceiptModal";
import {
  listProducts, productByBarcode, visualScan, createSale, isOnePay, getSubscriptionStatus,
  formatMXN, getStore, getUser, getSyncVersion,
} from "@/lib/api";
import {
  ScanLine, Sparkles, Plus, Minus, Trash2, Camera, Zap,
  Wallet, CreditCard, BookMarked, Check, Search, X, Radio,
} from "lucide-react";
import { toast, Toaster } from "sonner";
import useHardwareScanner from "@/hooks/useHardwareScanner";
import { beepSuccess, beepError } from "@/lib/beep";
import CaptureModeToggle, {
  readCaptureMode, persistCaptureMode,
  CAPTURE_MODE_VOICE, CAPTURE_MODE_SCANNER,
} from "@/components/chalann/CaptureModeToggle";

const QUICK_BARCODES = [
  { code: "7501055300075", label: "Coca lata" },
  { code: "7501030459286", label: "Pan Bimbo" },
  { code: "7501020544428", label: "Corona" },
  { code: "7501008445150", label: "Sabritas" },
];

export default function Cobrar() {
  const nav = useNavigate();
  const store = getStore();
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState([]);
  const [payment, setPayment] = useState("cash");
  const [customer, setCustomer] = useState("");
  const [saving, setSaving] = useState(false);
  const [scanValue, setScanValue] = useState("");
  const [scanState, setScanState] = useState({ kind: "idle" }); // idle | scanning | found | notfound | vision | vision-done
  const [visionResult, setVisionResult] = useState(null);
  const [qrSale, setQrSale] = useState(null);
  const onePay = isOnePay();
  const [captureMode, setCaptureMode] = useState(() => (isOnePay() ? CAPTURE_MODE_SCANNER : readCaptureMode()));
  const [subActive, setSubActive] = useState(true);
  useEffect(() => { getSubscriptionStatus().then((s) => setSubActive(!!s.is_active)).catch(() => {}); }, []);
  const scanInputRef = useRef(null);

  const switchCaptureMode = (m) => {
    if (m === captureMode) return;
    setCaptureMode(m);
    persistCaptureMode(m);
    if (m === CAPTURE_MODE_SCANNER) {
      toast("Modo Escáner activado", {
        description: "Enciende tu pistola Bluetooth · el micrófono queda apagado.",
        icon: "🔫",
      });
    } else {
      toast("Modo Voz activado", {
        description: "Di el nombre del producto o &ldquo;Chalán, cierra el turno&rdquo;.",
        icon: "🎙️",
      });
    }
  };

  useEffect(() => {
    if (!store?.id) { nav("/setup"); return; }
    (async () => {
      try { setProducts(await listProducts()); }
      catch { toast.error("No se pudo cargar el inventario"); }
    })();
    setTimeout(() => scanInputRef.current?.focus(), 400);
    // Multi-device sync: poll sync version; refresh products on bump.
    let lastVersion = 0;
    const sync = setInterval(async () => {
      try {
        const v = await getSyncVersion();
        if (v.version !== lastVersion) {
          if (lastVersion !== 0) {
            const fresh = await listProducts();
            setProducts(fresh);
          }
          lastVersion = v.version;
        }
      } catch { /* noop */ }
    }, 6000);
    return () => clearInterval(sync);
  }, [nav, store?.id]);

  const refreshProducts = async () => {
    try { setProducts(await listProducts()); } catch { /* noop */ }
  };

  // --- Cart helpers ---
  const addProductToCart = (p, qty = 1) => {
    setCart((c) => {
      const idx = c.findIndex((x) => x.product_id === p.id);
      if (idx >= 0) {
        const copy = [...c];
        copy[idx] = { ...copy[idx], quantity: copy[idx].quantity + qty };
        return copy;
      }
      return [...c, {
        product_id: p.id, name: p.name, unit_price: p.sale_price,
        quantity: qty, unit: p.unit || "pza", source: "barcode",
      }];
    });
  };

  const addLooseToCart = (item, qty = 1) => {
    setCart((c) => [
      ...c,
      {
        product_id: item.matched_product_id || null,
        name: item.name,
        unit_price: item.suggested_sale_price,
        quantity: qty,
        unit: item.unit || "pza",
        source: "vision",
        confidence: item.confidence,
      },
    ]);
  };

  const addVoiceItems = (items) => {
    setCart((c) => {
      const copy = [...c];
      for (const it of items) {
        // Bulk lines are unique per addition (weight-based) — do not merge.
        if (it.is_bulk) {
          copy.push({
            product_id: it.product_id,
            name: it.name,
            unit_price: it.unit_price,
            quantity: it.quantity,
            unit: it.unit || "kg",
            source: "voice",
            is_bulk: true,
            weight_kg: it.weight_kg,
            baseline_per_kg: it.baseline_per_kg,
            math_breakdown: it.math_breakdown,
            category: it.category,
          });
          continue;
        }
        const idx = copy.findIndex((x) => x.product_id === it.product_id && !x.is_bulk);
        if (idx >= 0) {
          copy[idx] = { ...copy[idx], quantity: copy[idx].quantity + it.quantity };
        } else {
          copy.push({
            product_id: it.product_id,
            name: it.name,
            unit_price: it.unit_price,
            quantity: it.quantity,
            unit: it.unit || "pza",
            source: "voice",
          });
        }
      }
      return copy;
    });
  };

  const changeQty = (i, delta) => {
    setCart((c) => {
      const copy = [...c];
      const line = copy[i];
      // Bulk lines step by 100 g; piece lines step by 1.
      const step = line.is_bulk ? 0.1 * Math.sign(delta) : delta;
      const q = Math.max(0, Number((line.quantity + step).toFixed(3)));
      if (q === 0) return copy.filter((_, idx) => idx !== i);
      const updated = { ...line, quantity: q };
      if (line.is_bulk) {
        updated.weight_kg = q;
        updated.math_breakdown = `${q} kg × $${(line.unit_price || 0).toFixed(2)}/kg = $${(q * line.unit_price).toFixed(2)}`;
      }
      copy[i] = updated;
      return copy;
    });
  };
  const removeLine = (i) => setCart((c) => c.filter((_, idx) => idx !== i));

  const total = cart.reduce((s, it) => s + it.quantity * it.unit_price, 0);

  // --- Barcode scan ---
  const submitScan = async (codeOverride) => {
    const code = (codeOverride ?? scanValue).trim();
    if (!code) return;
    setScanState({ kind: "scanning", code });
    try {
      const r = await productByBarcode(code);
      if (r.in_inventory && r.product) {
        addProductToCart(r.product, 1);
        setScanState({ kind: "found", product: r.product });
        beepSuccess();
        toast.success(`+ ${r.product.name}`);
        setScanValue("");
        setTimeout(() => setScanState({ kind: "idle" }), 1400);
      } else if (r.suggestion?.found) {
        setScanState({ kind: "notfound", code, suggestion: r.suggestion });
        beepError();
        toast.error(`${r.suggestion.name} no está en tu inventario`);
      } else {
        setScanState({ kind: "notfound", code });
        beepError();
        toast.error("Código no reconocido");
      }
    } catch (e) {
      setScanState({ kind: "notfound", code });
      beepError();
      toast.error("No se pudo consultar el código");
    } finally {
      setTimeout(() => scanInputRef.current?.focus(), 200);
    }
  };

  // Hardware HID (Bluetooth/USB) barcode scanner — only enabled in "scanner" mode.
  // When the cashier picks "voice" mode we silence the global keydown listener so
  // an accidentally-dropped gun can't dump digits into the page.
  useHardwareScanner({
    enabled: captureMode === CAPTURE_MODE_SCANNER,
    onScan: (gtin) => submitScan(gtin),
    minLen: 8, maxLen: 14, gapMs: 50,
  });

  // --- Visual scan ---
  const runVisualScan = async () => {
    setScanState({ kind: "vision" });
    setVisionResult(null);
    try {
      await new Promise((r) => setTimeout(r, 1400)); // dramatic CV moment
      const r = await visualScan();
      setVisionResult(r);
      setScanState({ kind: "vision-done" });
    } catch (e) {
      toast.error("No se pudo escanear visualmente");
      setScanState({ kind: "idle" });
    }
  };

  const acceptVision = () => {
    if (!visionResult) return;
    addLooseToCart(visionResult, 1);
    toast.success(`Detectado: ${visionResult.name}`);
    setVisionResult(null);
    setScanState({ kind: "idle" });
  };

  const registrarVenta = async () => {
    if (cart.length === 0) return toast.error("No hay nada en el ticket");
    if (payment === "credit" && !customer.trim()) return toast.error("Escribe el nombre del cliente del fiado");
    setSaving(true);
    try {
      const items = cart.map((c) => ({
        product_id: c.product_id, name: c.name, quantity: c.quantity,
        unit_price: c.unit_price, subtotal: c.unit_price * c.quantity,
      }));
      const sale = await createSale({
        items, total_amount: total, payment_method: payment,
        customer_name: customer || null,
        cashier_name: getUser()?.name || "Dueño",
      });
      toast.success(
        payment === "credit"
          ? `Fiado registrado · ${formatMXN(total)}`
          : `¡Venta registrada! ${formatMXN(total)}`
      );
      setCart([]); setCustomer(""); setScanValue(""); setScanState({ kind: "idle" });
      refreshProducts();
      setQrSale(sale);
      return sale;
    } catch (e) {
      toast.error("No se pudo registrar la venta");
    } finally { setSaving(false); }
  };

  // Voice command intents for QR receipt generation
  const receiptCommandDetected = (raw) => {
    const t = (raw || "").toLowerCase();
    return /\b(genera(?: el)? recibo|pide(?: el)? ticket|dale (?:su|la) nota|dame(?: el)? ticket|manda(?: el)? ticket|recibo por favor)\b/.test(t);
  };

  const addVoiceItemsWithCommand = (items, extra) => {
    const raw = extra?.rawText || "";
    if (receiptCommandDetected(raw)) {
      // If we have an unsaved cart, close it first then show QR
      if (cart.length > 0) {
        registrarVenta();
      } else if (qrSale) {
        // no-op; modal already open
      } else {
        toast("No hay venta que recibar todavía", { description: "Arma el ticket primero." });
      }
    }
    if (items?.length) addVoiceItems(items);
  };

  return (
    <main className="min-h-screen bg-[#050505] text-white" data-testid="cobrar">
      <div className="grain-overlay" />
      <DashNav />

      <div className="relative max-w-[1600px] mx-auto px-4 md:px-10 py-6 md:py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="font-mono text-[11px] tracking-[0.3em] text-white/40 uppercase mb-1">/ Cobrar</p>
            <h1 className="font-display text-3xl md:text-5xl tracking-tighter leading-none">
              {onePay ? <>Escanea, busca, <span className="text-[#ff5a00]">cobra.</span></> : <>Habla, escanea, <span className="text-[#ff5a00]">cobra.</span></>}
            </h1>
          </div>
          <div className="flex items-center gap-3">
            {!onePay && <CaptureModeToggle value={captureMode} onChange={switchCaptureMode} />}
            {!subActive ? null : captureMode === CAPTURE_MODE_SCANNER ? (
              <div
                className="inline-flex items-center gap-2 rounded-full border border-[#CCFF00]/40 bg-[#CCFF00]/[0.08] px-3 py-1.5"
                title="Pistola HID activa · el micrófono está apagado"
                data-testid="scanner-ready-indicator"
              >
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#CCFF00] opacity-70" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-[#CCFF00]" />
                </span>
                <Radio size={11} className="text-[#CCFF00]" />
                <span className="font-mono text-[10px] tracking-widest text-[#CCFF00] uppercase">Scanner Ready</span>
              </div>
            ) : (
              <div className="chip"><span className="rec-dot" /> Cámara activa</div>
            )}
          </div>
        </div>

        <SubscriptionGate>
        {/* Voice panel — full-width above the split. Hidden when scanner mode is active. */}
        {captureMode === CAPTURE_MODE_VOICE && (
          <div className="mb-6">
            <VoicePanel
              onItemsRecognized={addVoiceItems}
              onRawText={(raw) => {
                if (receiptCommandDetected(raw)) {
                  if (cart.length > 0) { registrarVenta(); }
                  else { toast("Arma un ticket primero para generar el recibo"); }
                }
              }}
            />
          </div>
        )}
        {captureMode === CAPTURE_MODE_SCANNER && (
          <div
            className="mb-6 rounded-2xl border border-[#ff5a00]/30 bg-[#ff5a00]/[0.05] px-5 py-4 flex items-center gap-3"
            data-testid="scanner-mode-banner"
          >
            <Radio size={18} className="text-[#ff5a00] flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="font-mono text-[10px] tracking-widest text-[#ff5a00] uppercase mb-0.5">
                Modo Escáner activo
              </p>
              <p className="text-sm text-white/70 leading-snug">
                {onePay
                  ? <>Enciende tu pistola Bluetooth y dispara los códigos, o escribe el nombre en el buscador. <Link to="/guia" className="underline text-[#ccff00]" data-testid="cobrar-guia-link">Ver guía de la pistola</Link>.</>
                  : "Enciende tu pistola Bluetooth y dispara los códigos. El micrófono queda apagado hasta que cambies a Modo Voz."}
              </p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* PHONE CAMERA VIEWPORT */}
          <section className="lg:col-span-7 flex justify-center">
            <PhoneFrame>
              <CameraViewport
                state={scanState}
                visionResult={visionResult}
                onAcceptVision={acceptVision}
                onDismissVision={() => { setVisionResult(null); setScanState({ kind: "idle" }); }}
                onDetectBarcode={(code) => { setScanValue(code); submitScan(code); }}
              />

              {/* Overlay top: barcode input */}
              <div className="absolute top-3 left-3 right-3 z-20">
                <form
                  onSubmit={(e) => { e.preventDefault(); submitScan(); }}
                  className="flex items-center gap-2 rounded-full bg-black/70 backdrop-blur-md border border-white/20 px-3 py-2"
                >
                  <ScanLine size={16} className="text-[#ccff00] flex-shrink-0" />
                  <input
                    ref={scanInputRef}
                    value={scanValue}
                    onChange={(e) => setScanValue(e.target.value.replace(/\s/g, ""))}
                    placeholder="Escanea o escribe el código…"
                    inputMode="numeric"
                    className="flex-1 bg-transparent outline-none font-mono text-sm placeholder-white/40"
                    data-testid="scan-input"
                  />
                  {scanValue && (
                    <button type="button" onClick={() => setScanValue("")} className="text-white/50 hover:text-white">
                      <X size={14} />
                    </button>
                  )}
                  <button
                    type="submit"
                    className="rounded-full bg-[#ccff00] text-black px-3 py-1 text-xs font-bold uppercase tracking-widest"
                    data-testid="scan-submit"
                  >
                    Buscar
                  </button>
                </form>
              </div>

              {/* Overlay bottom: quick codes + visual scan */}
              <div className="absolute bottom-3 left-3 right-3 z-20 space-y-2">
                <div className="flex flex-wrap gap-1.5">
                  {QUICK_BARCODES.map((b) => (
                    <button
                      key={b.code}
                      onClick={() => { setScanValue(b.code); submitScan(b.code); }}
                      className="px-2.5 py-1 rounded-full bg-black/70 backdrop-blur border border-white/15 text-[10px] font-mono tracking-widest text-white/80 hover:border-[#ccff00] hover:text-[#ccff00] transition-colors"
                      data-testid={`quick-${b.code}`}
                    >
                      {b.label}
                    </button>
                  ))}
                </div>
                {!onePay && (
                <button
                  onClick={runVisualScan}
                  disabled={scanState.kind === "vision"}
                  className="w-full rounded-2xl bg-black/70 backdrop-blur-md border border-[#ff5a00]/60 py-3 flex items-center justify-center gap-2 hover:bg-[#ff5a00]/20 transition-colors disabled:opacity-70"
                  data-testid="btn-visual-scan"
                >
                  <Sparkles size={16} className="text-[#ff5a00]" />
                  <span className="font-mono uppercase tracking-widest text-xs md:text-sm text-white">
                    {scanState.kind === "vision" ? "Analizando…" : "IA Visión · granel"}
                  </span>
                </button>
                )}
              </div>
            </PhoneFrame>
          </section>

          {/* CART PANEL */}
          <aside className="lg:col-span-5">
            <div className="rounded-3xl border border-white/10 bg-white/[0.02] p-5 md:p-6 sticky top-24" data-testid="cart-panel">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="font-mono text-[10px] tracking-widest text-white/40 uppercase">Ticket en curso</p>
                  <p className="font-display text-lg tracking-tighter mt-1">{cart.length} artículo{cart.length !== 1 && "s"}</p>
                </div>
                {cart.length > 0 && (
                  <button onClick={() => setCart([])} className="text-white/40 hover:text-[#ff5a00] text-xs font-mono uppercase tracking-widest" data-testid="clear-cart">
                    Limpiar
                  </button>
                )}
              </div>

              <div className="max-h-[46vh] overflow-auto pr-1 space-y-2">
                <AnimatePresence initial={false}>
                  {cart.length === 0 && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                      className="py-12 text-center text-white/40 text-sm border border-dashed border-white/10 rounded-2xl"
                    >
                      {onePay ? "Escanea un código con tu pistola o escribe el nombre." : "Escanea un código o usa la IA visual."}
                    </motion.div>
                  )}
                  {cart.map((it, i) => (
                    <motion.div
                      key={`${it.name}-${i}`}
                      layout
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 20 }}
                      transition={{ duration: 0.3 }}
                      className={`rounded-xl border px-3 py-2 flex items-center gap-2 ${
                        it.source === "vision"
                          ? "border-[#ff5a00]/40 bg-[#ff5a00]/[0.05]"
                          : it.source === "voice"
                          ? "border-[#ccff00]/40 bg-[#ccff00]/[0.05]"
                          : "border-white/5 bg-black/30"
                      }`}
                      data-testid={`cart-line-${i}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          {it.source === "vision" && <Sparkles size={10} className="text-[#ff5a00] flex-shrink-0" />}
                          {it.source === "voice" && <Sparkles size={10} className="text-[#ccff00] flex-shrink-0" />}
                          <p className="text-sm truncate">
                            {it.name}
                            {it.is_bulk && it.category && (
                              <span className="text-white/40 font-mono text-[10px] ml-1">({it.category})</span>
                            )}
                          </p>
                        </div>
                        {it.is_bulk ? (
                          <p className="text-[11px] text-[#ccff00] font-mono" data-testid={`bulk-breakdown-${i}`}>
                            {(it.weight_kg ?? it.quantity).toFixed(3)} kg @ {formatMXN(it.unit_price)}/kg = {formatMXN(it.unit_price * it.quantity)}
                          </p>
                        ) : (
                          <p className="text-[11px] text-white/40 font-mono">
                            {formatMXN(it.unit_price)}/{it.unit} · {formatMXN(it.unit_price * it.quantity)}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <button className="w-7 h-7 rounded-full border border-white/10 hover:bg-white/10" onClick={() => changeQty(i, -1)} data-testid={`cart-minus-${i}`}><Minus size={12} className="mx-auto" /></button>
                        <span className="w-14 text-center font-mono text-xs">
                          {it.is_bulk
                            ? `${(it.weight_kg ?? it.quantity).toFixed(3)} kg`
                            : it.quantity}
                        </span>
                        <button className="w-7 h-7 rounded-full border border-white/10 hover:bg-white/10" onClick={() => changeQty(i, 1)} data-testid={`cart-plus-${i}`}><Plus size={12} className="mx-auto" /></button>
                        <button className="w-7 h-7 rounded-full text-white/40 hover:text-[#ff5a00]" onClick={() => removeLine(i)}><Trash2 size={12} className="mx-auto" /></button>
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>

              <div className="mt-5 pt-5 border-t border-white/10">
                <div className="flex justify-between items-baseline">
                  <span className="text-white/60 text-sm">Total MXN</span>
                  <motion.span
                    key={total}
                    initial={{ scale: 1.12 }} animate={{ scale: 1 }}
                    transition={{ duration: 0.3, type: "spring" }}
                    className="font-display text-4xl md:text-5xl tracking-tighter"
                    data-testid="cart-total"
                  >
                    {formatMXN(total)}
                  </motion.span>
                </div>
              </div>

              {/* Payment method */}
              <div className="mt-5">
                <p className="font-mono text-[10px] tracking-widest text-white/40 uppercase mb-2">Método de pago</p>
                <div className="grid grid-cols-3 gap-2">
                  <PayPill icon={Wallet} label="Efectivo" active={payment === "cash"} onClick={() => setPayment("cash")} testid="pay-cash" />
                  <PayPill icon={CreditCard} label="Tarjeta" active={payment === "card"} onClick={() => setPayment("card")} testid="pay-card" />
                  <PayPill icon={BookMarked} label="Fiado" active={payment === "credit"} onClick={() => setPayment("credit")} testid="pay-credit" />
                </div>
                {payment === "credit" && (
                  <input
                    value={customer}
                    onChange={(e) => setCustomer(e.target.value)}
                    placeholder="Nombre del cliente"
                    className="w-full mt-3 bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-sm placeholder-white/30 focus:outline-none focus:border-[#ff5a00]"
                    data-testid="pay-customer"
                  />
                )}
              </div>

              {/* Big green button */}
              <button
                onClick={registrarVenta}
                disabled={saving || cart.length === 0}
                className="mt-5 w-full py-5 rounded-2xl font-display text-2xl tracking-tighter text-black bg-[#7dff5a] hover:bg-[#9dff85] active:scale-[0.99] disabled:opacity-40 disabled:hover:bg-[#7dff5a] flex items-center justify-center gap-3 transition-colors duration-200 shadow-[0_20px_60px_-15px_rgba(125,255,90,0.6)]"
                style={{ transitionProperty: "background-color, transform, opacity" }}
                data-testid="registrar-venta"
              >
                <Check size={22} strokeWidth={3} />
                Confirmar y Cobrar
                <span className="font-mono text-sm opacity-70">· {formatMXN(total)}</span>
              </button>

              {/* Generar Ticket QR — visible when last sale exists but modal closed */}
              {qrSale && (
                <button
                  onClick={() => setQrSale(qrSale)}
                  className="mt-3 w-full py-3 rounded-2xl border border-[#ccff00]/50 bg-[#ccff00]/10 hover:bg-[#ccff00]/20 text-[#ccff00] font-bold flex items-center justify-center gap-2 transition-colors"
                  data-testid="qr-reopen"
                >
                  <Sparkles size={16} /> Volver a mostrar Ticket QR
                </button>
              )}
            </div>
          </aside>
        </div>
        </SubscriptionGate>

        <p className="mt-8 text-center text-white/40 text-xs font-mono tracking-widest uppercase">
          {onePay
            ? <>Consejo: dispara el código con tu pistola o escribe el nombre en <span className="text-[#ccff00]">Escanea o escribe</span> · <Link to="/guia" className="text-[#ff5a00] underline">guía de operación</Link>.</>
            : <>Consejo: dile <span className="text-[#ccff00]">&ldquo;suma dos Cocas y un pan Bimbo&rdquo;</span> · o presiona <span className="text-[#ff5a00]">IA Visión</span> para granel.</>}
        </p>
      </div>
      <Toaster theme="dark" position="top-center" />
      <QRReceiptModal
        open={!!qrSale}
        sale={qrSale}
        onNext={() => setQrSale(null)}
      />
    </main>
  );
}

/* ---- Sub components ---- */

function PhoneFrame({ children }) {
  return (
    <div className="relative w-full max-w-[380px] aspect-[9/17] rounded-[44px] border border-white/15 bg-black shadow-[0_40px_120px_-20px_rgba(255,90,0,0.35),0_10px_40px_rgba(0,0,0,0.7)] p-2" data-testid="phone-frame">
      {/* Notch */}
      <div className="absolute top-2 left-1/2 -translate-x-1/2 w-24 h-6 bg-black rounded-b-2xl z-30 flex items-center justify-center gap-1">
        <span className="w-1.5 h-1.5 rounded-full bg-white/20" />
        <span className="w-6 h-1.5 rounded-full bg-white/10" />
      </div>
      <div className="relative w-full h-full rounded-[36px] overflow-hidden bg-[#0a0a0a]">
        {children}
      </div>
    </div>
  );
}

function CameraViewport({ state, visionResult, onAcceptVision, onDismissVision, onDetectBarcode }) {
  const [cameraOn, setCameraOn] = useState(false);
  const [cameraErr, setCameraErr] = useState(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const detectorRef = useRef(null);
  const rafRef = useRef(null);
  const lastCodeRef = useRef({ code: null, ts: 0 });

  useEffect(() => {
    return () => stopCam();
  }, []);

  const startCam = async () => {
    setCameraErr(null);
    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraErr("Este navegador no permite abrir la cámara");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" }, width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
      }
      setCameraOn(true);
      // Barcode detector
      if ("BarcodeDetector" in window) {
        try {
          detectorRef.current = new window.BarcodeDetector({
            formats: ["ean_13", "ean_8", "upc_a", "upc_e", "code_128", "code_39", "qr_code"],
          });
          scanLoop();
        } catch { detectorRef.current = null; }
      }
    } catch (e) {
      setCameraErr("Permiso denegado o cámara no disponible");
      setCameraOn(false);
    }
  };

  const stopCam = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    const s = streamRef.current;
    if (s) s.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    setCameraOn(false);
  };

  const scanLoop = async () => {
    const vid = videoRef.current;
    const det = detectorRef.current;
    if (!vid || !det || !streamRef.current) return;
    try {
      if (vid.readyState >= 2) {
        const codes = await det.detect(vid);
        if (codes && codes.length) {
          const code = codes[0].rawValue;
          const now = Date.now();
          if (code && (code !== lastCodeRef.current.code || now - lastCodeRef.current.ts > 2500)) {
            lastCodeRef.current = { code, ts: now };
            onDetectBarcode?.(code);
          }
        }
      }
    } catch { /* frame miss */ }
    rafRef.current = requestAnimationFrame(scanLoop);
  };

  return (
    <div className="relative w-full h-full overflow-hidden bg-[#050505]">
      {/* Real camera video */}
      {cameraOn && (
        <video
          ref={videoRef}
          className="absolute inset-0 w-full h-full object-cover z-0"
          playsInline
          muted
          data-testid="camera-video"
        />
      )}

      {/* Simulated camera feed (fallback / when off) */}
      {!cameraOn && (
        <div className="absolute inset-0">
          <div className="absolute inset-0 opacity-70"
            style={{
              background:
                "radial-gradient(circle at 30% 40%, #1a1207 0%, #050505 55%), radial-gradient(circle at 70% 70%, #0a1010 0%, transparent 55%)",
            }}
          />
          {Array.from({ length: 18 }).map((_, i) => (
            <motion.span
              key={i}
              className="absolute rounded-full bg-white/10"
              style={{
                width: 1 + (i % 3),
                height: 1 + (i % 3),
                left: `${(i * 13) % 100}%`,
                top: `${(i * 37) % 100}%`,
              }}
              animate={{ y: [0, 12, 0], opacity: [0.1, 0.4, 0.1] }}
              transition={{ duration: 4 + (i % 5), repeat: Infinity, ease: "easeInOut", delay: i * 0.2 }}
            />
          ))}
          <div className="absolute inset-0"
            style={{
              backgroundImage:
                "linear-gradient(to right, rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.03) 1px, transparent 1px)",
              backgroundSize: "24px 24px",
            }}
          />
        </div>
      )}

      {/* Vignette */}
      <div className="absolute inset-0 pointer-events-none z-[5]"
        style={{ boxShadow: "inset 0 0 120px rgba(0,0,0,0.9)" }} />

      {/* Center reticle */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-[6]">
        <Reticle state={state} />
      </div>

      {/* Camera toggle pill */}
      <div className="absolute top-14 left-1/2 -translate-x-1/2 z-30">
        <button
          onClick={cameraOn ? stopCam : startCam}
          className={`text-[10px] font-mono uppercase tracking-widest px-3 py-1.5 rounded-full border backdrop-blur-md ${
            cameraOn
              ? "bg-[#ccff00] text-black border-[#ccff00]"
              : "bg-black/70 text-white/80 border-white/20 hover:border-[#ccff00] hover:text-[#ccff00]"
          }`}
          data-testid="camera-toggle"
        >
          {cameraOn ? "Cámara ON · toca para parar" : "Encender cámara real"}
        </button>
        {cameraErr && (
          <p className="mt-1 text-center text-[10px] font-mono text-[#ff5a00] max-w-[220px]">{cameraErr}</p>
        )}
      </div>

      {/* Scan line during scanning */}
      <AnimatePresence>
        {state.kind === "scanning" && (
          <motion.div
            key="scanline"
            initial={{ y: "-100%" }} animate={{ y: "100%" }} exit={{ opacity: 0 }}
            transition={{ duration: 1.1, ease: "easeInOut" }}
            className="absolute left-0 right-0 h-[2px] bg-[#ccff00] shadow-[0_0_16px_#ccff00] z-10"
          />
        )}
      </AnimatePresence>

      {/* Vision pulse ring */}
      <AnimatePresence>
        {state.kind === "vision" && <VisionPulse />}
      </AnimatePresence>

      {/* Vision result */}
      <AnimatePresence>
        {state.kind === "vision-done" && visionResult && (
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}
            className="absolute inset-x-4 top-1/2 -translate-y-1/2 z-30"
            data-testid="vision-result"
          >
            <div className="rounded-2xl bg-black/85 backdrop-blur-md border border-[#ff5a00]/50 p-4">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles size={12} className="text-[#ff5a00]" />
                <span className="font-mono text-[10px] tracking-widest uppercase text-white/70">
                  IA Visión · {Math.round(visionResult.confidence * 100)}% seguro
                </span>
              </div>
              <p className="font-display text-2xl tracking-tighter leading-tight">{visionResult.name}</p>
              <p className="font-mono text-[11px] text-white/60 mt-1">
                {visionResult.category} · {formatMXN(visionResult.suggested_sale_price)}/{visionResult.unit}
              </p>
              {visionResult.matched_product_id ? (
                <p className="text-[11px] text-[#ccff00] mt-2">✓ Ya está en tu inventario</p>
              ) : (
                <p className="text-[11px] text-white/50 mt-2">Se agregará como venta a granel</p>
              )}
              <div className="mt-4 flex gap-2">
                <button
                  onClick={onAcceptVision}
                  className="flex-1 rounded-full bg-[#ff5a00] text-black py-2 font-bold text-sm flex items-center justify-center gap-2"
                  data-testid="vision-accept"
                >
                  <Plus size={14} /> Agregar al ticket
                </button>
                <button
                  onClick={onDismissVision}
                  className="rounded-full border border-white/20 px-4 py-2 text-sm text-white/70"
                  data-testid="vision-dismiss"
                >
                  Reintentar
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Barcode found / not-found overlay */}
      <AnimatePresence>
        {state.kind === "found" && (
          <motion.div
            initial={{ scale: 0.7, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.8, opacity: 0 }}
            className="absolute inset-x-8 top-1/2 -translate-y-1/2 z-30 rounded-2xl bg-[#ccff00]/95 text-black p-4 shadow-[0_20px_60px_rgba(204,255,0,0.4)]"
          >
            <div className="flex items-center gap-2">
              <Check size={20} strokeWidth={3} />
              <p className="font-display text-lg tracking-tight leading-tight">{state.product?.name}</p>
            </div>
            <p className="font-mono text-xs mt-1">Agregado · {formatMXN(state.product?.sale_price)}</p>
          </motion.div>
        )}
        {state.kind === "notfound" && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="absolute inset-x-8 top-1/2 -translate-y-1/2 z-30 rounded-2xl bg-black/90 border border-[#ff5a00] p-4"
            data-testid="scan-notfound"
          >
            <p className="font-mono text-[10px] tracking-widest uppercase text-[#ff5a00] mb-1">Código no encontrado</p>
            <p className="text-sm text-white/80 leading-snug">
              {state.suggestion?.name
                ? <>Es <strong>{state.suggestion.name}</strong>, pero no está en tu inventario. Dalo de alta primero.</>
                : "Este código no está en tu inventario ni en nuestro catálogo."}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function Reticle({ state }) {
  const active = state.kind === "scanning" || state.kind === "vision";
  const color = state.kind === "vision" ? "#ff5a00" : "#ccff00";
  return (
    <motion.div
      className="relative w-56 h-56"
      animate={{ scale: active ? [1, 1.05, 1] : 1 }}
      transition={{ duration: 1.2, repeat: active ? Infinity : 0 }}
    >
      {[
        ["top-0 left-0", "border-t-2 border-l-2"],
        ["top-0 right-0", "border-t-2 border-r-2"],
        ["bottom-0 left-0", "border-b-2 border-l-2"],
        ["bottom-0 right-0", "border-b-2 border-r-2"],
      ].map(([pos, borders], i) => (
        <span key={i} className={`absolute w-8 h-8 ${pos} ${borders} rounded-[6px]`}
          style={{ borderColor: color, filter: active ? `drop-shadow(0 0 8px ${color})` : "none" }} />
      ))}
      <div className="absolute inset-0 flex items-center justify-center">
        {state.kind === "idle" && (
          <div className="text-center opacity-60">
            <Camera size={26} className="mx-auto text-white/60" />
            <p className="font-mono text-[10px] uppercase tracking-widest text-white/40 mt-2">Apunta al código</p>
          </div>
        )}
        {state.kind === "vision" && (
          <div className="text-center">
            <Zap size={26} className="mx-auto text-[#ff5a00] animate-pulse" />
            <p className="font-mono text-[10px] uppercase tracking-widest text-[#ff5a00] mt-2">Reconociendo…</p>
          </div>
        )}
      </div>
    </motion.div>
  );
}

function VisionPulse() {
  return (
    <>
      {[0, 0.3, 0.6].map((delay, i) => (
        <motion.span
          key={i}
          className="absolute inset-0 pointer-events-none z-10"
          initial={{ opacity: 0.4 }}
          animate={{ opacity: 0 }}
          transition={{ duration: 1.4, repeat: Infinity, delay }}
          style={{ boxShadow: "inset 0 0 60px rgba(255,90,0,0.6)" }}
        />
      ))}
    </>
  );
}

function PayPill({ icon: Icon, label, active, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`rounded-xl border py-2.5 flex flex-col items-center gap-1 transition-colors duration-200 ${
        active
          ? "bg-white text-black border-white"
          : "border-white/10 text-white/70 hover:bg-white/[0.04]"
      }`}
    >
      <Icon size={16} />
      <span className="text-[11px] font-mono uppercase tracking-widest">{label}</span>
    </button>
  );
}
